import { use } from 'react';
import { UNSAFE_DataRouterContext, matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { FootballLogo } from '#src/atoms/icons/FootballLogo.tsx';
import { MLBLogo } from '#src/atoms/icons/MLBLogo.tsx';
import { NBALogo } from '#src/atoms/icons/NBALogo.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandM } from '#src/atoms/typography/index.tsx';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_MY_CLUB,
  ANY_SPORT_SERIES,
} from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import {
  SPORT_PATH_MAP,
  generateSportPath,
  matchAnySportPath,
} from '#src/lib/routing/generateSportPath.ts';
import { getPathPattern } from '#src/lib/routing/getPathPattern.ts';

import { SportButtonContent } from './SportButtonContent/index.tsx';
import { SportLink } from './SportLink/index.tsx';

const DropdownContent = styled(Vertical)`
  padding: var(--double-unit);
  background-color: var(--c-surface-background);
`;

const isMyClub = (pathname: string) => {
  return matchAnySportPath(catchAll(ANY_SPORT_MY_CLUB), pathname);
};

const isSeriesPath = (pathname: string) =>
  matchAnySportPath(catchAll(ANY_SPORT_SERIES), pathname);

const useGetSwitchSportPath = () => {
  const { pathname } = useLocation();
  const { sport: currentSport } = useSportContext();
  const { currentUser } = useCurrentUserContext();

  const isSportPath = Object.values(SPORT_PATH_MAP).some(path =>
    matchPath(catchAll(path), pathname)
  );

  const { router } = use(UNSAFE_DataRouterContext) ?? {};

  const pathPattern = getPathPattern(router?.routes ?? [], pathname);
  const pathWithoutParam = pathPattern.split(':').at(0);

  return (sport: Sport) => {
    if (isSportPath && sport !== currentSport && pathWithoutParam) {
      const pathWithoutParamAndSport = matchPath(
        catchAll(SPORT_PATH_MAP[currentSport]),
        pathWithoutParam
      )?.params['*'];

      // keeps the pathname params if it's my club to avoid redirecting back to current user's my club
      if (isMyClub(pathname)) {
        const pathWithoutSport = matchPath(
          catchAll(SPORT_PATH_MAP[currentSport]),
          pathname
        )?.params['*'];

        if (pathWithoutSport) {
          return generateSportPath(pathWithoutSport, { sport });
        }
      }
      if (pathWithoutParamAndSport) {
        const firstLevelPath = pathWithoutParamAndSport.split('/').at(0) ?? '';
        const targetPath = generateSportPath(firstLevelPath, {
          sport,
        });

        const targetSportProfileBySport = {
          [Sport.FOOTBALL]: currentUser?.footballUserProfile,
          [Sport.NBA]: currentUser?.nbaUserProfile,
          [Sport.BASEBALL]: currentUser?.baseballUserProfile,
        };

        if (
          !targetSportProfileBySport[sport]?.proUnlocked &&
          !isSeriesPath(targetPath)
        ) {
          return generateSportPath(ANY_SPORT_SERIES, { sport });
        }
        if (
          targetSportProfileBySport[sport]?.proUnlocked &&
          isSeriesPath(targetPath)
        ) {
          return generateSportPath(ANY_SPORT_HOME, { sport });
        }

        return targetPath;
      }
    }
    return SPORT_PATH_MAP[sport];
  };
};

type Props = {
  displaySelectedSportName?: boolean;
  withoutChevronIcon?: boolean;
};
export const SportSelector = ({
  displaySelectedSportName,
  withoutChevronIcon,
}: Props) => {
  const getSwitchSportPath = useGetSwitchSportPath();
  return (
    <Dropdown
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <SportButtonContent
            displaySelectedSportName={displaySelectedSportName}
            withoutChevronIcon={withoutChevronIcon}
            active={open}
          />
        </button>
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          <SportLink
            to={getSwitchSportPath(Sport.FOOTBALL)}
            icon={<FootballLogo width={45} height={45} />}
            title={<BrandM bold>Football</BrandM>}
            onClick={closeDropdown}
          />
          <SportLink
            to={getSwitchSportPath(Sport.NBA)}
            icon={<NBALogo width={45} height={45} />}
            title={<BrandM bold>NBA</BrandM>}
            onClick={closeDropdown}
          />
          <SportLink
            to={getSwitchSportPath(Sport.BASEBALL)}
            icon={<MLBLogo width={45} height={45} />}
            title={<BrandM bold>MLB</BrandM>}
            onClick={closeDropdown}
          />
        </DropdownContent>
      )}
    </Dropdown>
  );
};
